/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MediaSolutionBatches, SolutionLotDetail, ValidationErrors } from "../types/mediaBatchPreparation.types";
import { MediaBatchPreparationInitServices } from "../../../../service/mediaBatchPrepInitServices";


// ── Types ────────────────────────────────────────────────────────────────────

export type AdditiveCategory = string;

export interface AdditivePayload {
  rowId: number;
  category: AdditiveCategory;
  batchId: string;
  batchName: string;
  lotId: string;
  lotCode: string;
  quantity: string;
}

interface AdditiveRow {
  rowId: number;
  category: AdditiveCategory;
  batchId: string;
  batchName: string;
  lotId: string;
  lotCode: string;
  quantity: string;
}

interface RowMeta {
  lots: SolutionLotDetail[];
  isLoadingLots: boolean;
}

// ── Split helper — maps rows → { additives, reagents } ───────────────────────
// Neutralizer → additives[], Supplementary → reagents[]
export interface SplitAdditives {
  additives: { batch_id: number; lot_id: number; quantity: number }[];
  reagents:  { batch_id: number; lot_id: number; quantity: number }[];
}

const splitRows = (rows: AdditiveRow[]): SplitAdditives => {
  const toEntry = (row: AdditiveRow) => ({
    batch_id: Number(row.batchId),
    lot_id: Number(row.lotId),
    quantity: Number(row.quantity),
  });

  return {
    additives: rows
      .filter((r) => r.category === "Supplement")
      .map(toEntry),

    reagents: rows
      .filter((r) => r.category === "Neutralizer")
      .map(toEntry),
  };
};

// ── Props ─────────────────────────────────────────────────────────────────────
interface Props {
  onChange?: (split: SplitAdditives) => void;
  errors: ValidationErrors;
  setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const createEmptyRow = (): AdditiveRow => ({
  rowId:    Date.now(),
  category: "",
  batchId:  "",
  batchName:"",
  lotId:    "",
  lotCode:  "",
  quantity: "",
});

// ── Component ─────────────────────────────────────────────────────────────────
const OptionalAdditives = ({ onChange, errors, setErrors }: Props) => {
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded]           = useState(false);
  const [rows, setRows]                       = useState<AdditiveRow[]>([createEmptyRow()]);
  const [batches, setBatches]                 = useState<MediaSolutionBatches[]>([]);
  const [isBatchLoading, setIsBatchLoading]   = useState(false);
  const [rowMeta, setRowMeta]                 = useState<Record<number, RowMeta>>({});

  // Notify parent with split payload on every row change
  const notifyParent = (updatedRows: AdditiveRow[]) => {
    onChange?.(splitRows(updatedRows));
  };

  useEffect(() => {
    if (isExpanded) {
      fetchCategories();
    }
  }, [isExpanded]);


  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  
  const fetchCategories = async () => {
    try {
      const response =
        await MediaBatchPreparationInitServices.getCategoryList();

      setCategories(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  

  const fetchBatches = async (category: string) => {
    setIsBatchLoading(true);
    try {
      const response = await MediaBatchPreparationInitServices.getMediaSolutionBatches({
        page: 1, size: 50, sort: "id", order: "desc", status: "Released", category: category,
      });
      setBatches(response.data as MediaSolutionBatches[]);
    } catch (err) {
      console.error("Failed to fetch batches:", err);
    } finally {
      setIsBatchLoading(false);
    }
  };

  const fetchLotsForRow = async (rowId: number, batchId: string) => {
    setRowMeta(prev => ({ ...prev, [rowId]: { lots: [], isLoadingLots: true } }));
    try {
      const response = await MediaBatchPreparationInitServices.getMediaSolutionBatchLots({
        solution_batch_id: batchId,
      });
      setRowMeta(prev => ({
        ...prev,
        [rowId]: { lots: response.data as SolutionLotDetail[], isLoadingLots: false },
      }));
    } catch (err) {
      console.error("Failed to fetch lots:", err);
      setRowMeta(prev => ({ ...prev, [rowId]: { lots: [], isLoadingLots: false } }));
    }
  };

  // ── Row handlers ──────────────────────────────────────────────────────────

  const handleCategoryChange = (
    rowId: number,
    category: AdditiveCategory
  ) => {
    const updated = rows.map((row) =>
      row.rowId === rowId
        ? {
            ...row,
            category,
            batchId: "",
            batchName: "",
            lotId: "",
            lotCode: "",
            quantity: "",
          }
        : row
    );

    setRows(updated);
    notifyParent(updated);

    setBatches([]);
    fetchBatches(category);
  };

  const handleBatchChange = (rowId: number, batchId: string) => {
    const selected = batches.find((b) => b.id === batchId);
    const updated = rows.map((row) =>
      row.rowId === rowId
        ? { ...row, batchId: selected?.id ?? "", batchName: selected?.batch_name ?? "", lotId: "", lotCode: "" }
        : row
    );
    setRows(updated);
    notifyParent(updated);
    if (selected) fetchLotsForRow(rowId, selected.id);
  };

  const handleLotChange = (rowId: number, lotId: string) => {
    const selected = rowMeta[rowId]?.lots.find((l) => l.id === lotId);
    const updated = rows.map((row) =>
      row.rowId === rowId
        ? { ...row, lotId: selected?.id ?? "", lotCode: selected?.lot_code ?? "" }
        : row
    );
    setRows(updated);
    notifyParent(updated);
  };

  const handleQuantityChange = (rowId: number, value: string) => {
    const updated = rows.map((row) =>
      row.rowId === rowId ? { ...row, quantity: value } : row
    );
    setRows(updated);
    notifyParent(updated);
    
    if (value.trim() !== "") {
    setErrors((prev) => ({
        ...prev,
        additiveQuantity: "",
      }));
    }
  };

  const addRow = () => {
    const updated = [...rows, createEmptyRow()];
    setRows(updated);
    notifyParent(updated);
  };

  const removeRow = (rowId: number) => {
    const updated = rows.filter((row) => row.rowId !== rowId);
    setRows(updated);
    notifyParent(updated);
    setRowMeta((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

      {/* Header */}
      <div
        className="px-6 py-2 border-b border-slate-100 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-[#2D3E50] text-white flex items-center justify-center text-[10px] font-black">
            03
          </div>
          <h3 className="text-[10px] font-black text-[#2D3E50] uppercase tracking-widest mt-2">
            {t("mediaBatchInit.preparation.optionalAdditiviesReagents")}
          </h3>
        </div>
        <span className={`material-symbols-outlined text-slate-400 transition-transform duration-300 ${isExpanded ? "" : "rotate-180"}`}>
          expand_less
        </span>
      </div>

      {/* Body */}
      {isExpanded && (
        <div className="animate-in slide-in-from-top-2 duration-300">
          <table className="w-full text-left">
            <thead className="bg-[#2D3E50] text-white text-[9px] font-black uppercase tracking-widest">
              <tr>
                <th className="px-6 py-3">{t("mediaBatchInit.preparation.component")}</th>
                <th className="px-6 py-3">{t("mediaBatchInit.preparation.batch")}</th>
                <th className="px-6 py-3">{t("mediaBatchInit.preparation.lotNumber")}</th>
                <th className="px-6 py-3">{t("mediaBatchInit.preparation.quantity")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const meta = rowMeta[row.rowId];
                return (
                  <tr key={row.rowId} className="border-b border-slate-100">

                    {/* Category — drives the Neutralizer/Supplementary split */}
                    <td className="px-4 py-3">
                      <select
                        value={row.category}
                        onChange={(e) => handleCategoryChange(row.rowId, e.target.value as AdditiveCategory)}
                        className="w-full px-4 py-2 bg-white border border-slate-200 rounded text-xs outline-none"
                      >
                        {/* <option value="">Select Category</option>
                        {categories.map((category) => (
                          <option
                            key={category.id}
                            value={category.name}
                          >
                            {category.name}
                          </option>
                        ))} */}
                        
                        <option value="">Select Category</option>
                        <option value="Neutralizer">Neutralizer</option>
                        <option value="Supplement">Supplements</option>
                      </select>
                    </td>

                    {/* Batch — disabled until category is picked */}
                    <td className="px-4 py-3">
                      <select
                        value={row.batchId}
                        onChange={(e) => handleBatchChange(row.rowId, e.target.value)}
                        disabled={!row.category || isBatchLoading}
                        className="w-full px-4 py-2 bg-white border border-slate-200 rounded text-xs outline-none disabled:opacity-50"
                      >
                        <option value="">
                          {isBatchLoading ? "Loading..." : "Select Batch"}
                        </option>
                        {batches.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.batch_name} — {b.solution_name}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Lot — disabled until batch is selected */}
                    <td className="px-4 py-2.5">
                      <select
                        value={row.lotId}
                        onChange={(e) => handleLotChange(row.rowId, e.target.value)}
                        disabled={!row.batchId || meta?.isLoadingLots}
                        className="w-full px-4 py-2 bg-white border border-slate-200 rounded text-xs outline-none disabled:opacity-50"
                      >
                        <option value="">
                          {meta?.isLoadingLots ? "Loading..." : "Select Lot"}
                        </option>
                        {meta?.lots.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.lot_code} — {l.solution_product_name}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Quantity + Remove */}
                    <td className="px-4 py-3.5 -mt-4">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            placeholder="0.00"
                            value={row.quantity}
                            onChange={(e) => handleQuantityChange(row.rowId, e.target.value)}
                            className={`w-24 px-4 py-2 rounded text-xs text-right outline-none ${
                              errors.additiveQuantity
                                ? "border border-red-500"
                                : "border border-slate-200"
                            }`}
                          />

                          <span className="text-[10px] font-black text-slate-400">ML</span>

                          <button
                            onClick={() => removeRow(row.rowId)}
                            className="material-symbols-outlined text-slate-300 text-lg hover:text-red-500 border-none"
                          >
                            cancel
                          </button>
                        </div>

                        <div className="h-0 mt-1">
                          {errors.additiveQuantity && (
                            <p className="text-red-500 text-[11px] leading-none">
                              {errors.additiveQuantity}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Add Row */}
          <div className="p-6">
            <button
              onClick={addRow}
              className="flex items-center gap-2 px-6 py-2.5 border-2 border-orange-500 text-orange-500 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-orange-50 transition-all"
            >
              <span className="material-symbols-outlined text-sm font-black">add_circle</span>
              {t("mediaBatchInit.preparation.addComponent")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptionalAdditives;
